import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Mail, Phone, Globe, Facebook, Instagram } from "lucide-react";

const About = () => {
  const founders = [
    {
      name: "An Cao",
      role: "Co-Founder, CEO",
      image: "/team/ancao.png",
      education: "Graduate in Computer Science & Mathematics from Vanderbilt University (#19)",
      responsibility: "Responsible for product strategy, technology direction, and overall operations."
    },
    {
      name: "Binh Ho",
      role: "Co-Founder, CTO",
      image: "/team/binhho.png",
      education: "Also graduated in CS & Math from Vanderbilt University, received offers from major corporations including Amazon, TikTok, Roblox, Goldman Sachs, Bank of America",
      responsibility: "Leading technology, system architecture, and security."
    },
    {
      name: "Mattomo",
      role: "Co-Founder, CMO",
      image: "/team/mattomo.png",
      education: "Influencer with over 1 million followers, brought in 100+ million views for Mai Tech's content ecosystem and partners",
      responsibility: "In charge of marketing, branding, and media development."
    },
    {
      name: "Phone Aye",
      role: "Advisor",
      image: "/team/phone.jpg",
      education: "Graduate in Computer Science from Vanderbilt University, currently Software Engineer at Meta, also received offer from Amazon",
      responsibility: "Providing strategic technical guidance and industry insights to drive innovation and growth."
    }
  ];

  const products = [
    {
      title: "AI-integrated management & sales system for RiceGo F&B chain",
      description: "with over 10 branches",
      link: "https://ricego.vn",
      linkText: "ricego.vn"
    },
    {
      title: "Smart multilingual website (4 languages), appointment booking app, and AI doctor for Ngoc Minh Clinic",
      link: "https://pkdkngocminh.vn",
      linkText: "pkdkngocminh.vn"
    },
    {
      title: "Mai Chat Agent",
      description: "AI platform for automated messaging, order closing, and 24/7 appointment scheduling",
      link: "https://maichatagent.com",
      linkText: "maichatagent.com"
    },
    {
      title: "Mai Talk",
      description: "English-learning social media app connecting learners worldwide",
      link: "https://maitalkapp.com",
      linkText: "maitalkapp.com"
    },
    {
      title: "MaiFit",
      description: "Fitness community social media app for health enthusiasts and fitness lovers",
      link: "https://maifitapp.com",
      linkText: "maifitapp.com"
    }
  ];

  const socialLinks = [
    {
      name: "Facebook",
      url: "https://www.facebook.com/maitechcomp",
      icon: Facebook
    },
    {
      name: "Instagram", 
      url: "https://www.instagram.com/maitechcorp",
      icon: Instagram
    },
    {
      name: "TikTok",
      url: "https://www.tiktok.com/@maitechcorp",
      text: "TikTok"
    },
    {
      name: "Zalo",
      url: "https://zalo.me/2617993914803033984",
      text: "Zalo"
    }
  ];

  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground">
      <Helmet>
        <title>About Us - Mai Tech | Leading AI & Technology Solutions in Vietnam</title>
        <meta 
          name="description" 
          content="Learn about Mai Tech - Vietnam's premier technology company specializing in AI solutions, app development, data analytics, and business automation." 
        />
        <meta 
          name="keywords" 
          content="Mai Tech, AI solutions Vietnam, technology company, app development, data analytics, automation, An Cao, Binh Ho, Mattomo" 
        />
        <meta property="og:title" content="About Us - Mai Tech" />
        <meta 
          property="og:description" 
          content="Meet the founders of Mai Tech and learn about our mission to provide cutting-edge AI and technology solutions in Vietnam." 
        />
        <meta property="og:image" content="/og-image.png" />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://maitechcorp.com/en/about" />
      </Helmet>
      
      <Navbar />
      
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-maitech-900 via-maitech-800 to-black py-20 md:py-32">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              {/* Mai Tech Logo */}
              <div className="mb-8">
                <img
                  src="/logo/maitechsolutions.png"
                  alt="Mai Tech Solutions Logo"
                  className="w-32 h-32 mx-auto mb-6 object-contain"
                />
              </div>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-primary to-purple-500">
                About Mai Tech
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
                Mai Tech is a technology company in Vietnam, specializing in providing solutions for app development, 
                websites, AI, data analytics, automation and management. We apply the latest and most advanced technologies 
                to help businesses grow rapidly, intelligently and efficiently.
              </p>
              <div className="mt-8">
                <a 
                  href="https://maitechcorp.com/en" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-primary hover:text-primary/80 transition-colors"
                >
                  <Globe className="w-5 h-5 mr-2" />
                  maitechcorp.com/en
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Founders Section */}
        <section className="py-20 bg-maitech-900/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                🌟 Founding Team
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
              {founders.map((founder, index) => (
                <div key={index} className="bg-maitech-800/50 backdrop-blur rounded-xl p-6 border border-maitech-700/50 hover:border-primary/50 transition-colors">
                  <div className="text-center mb-6">
                    <img
                      src={founder.image}
                      alt={founder.name}
                      className="w-24 h-24 lg:w-32 lg:h-32 rounded-full mx-auto mb-4 object-cover border-4 border-primary/20"
                    />
                    <h3 className="text-xl lg:text-2xl font-bold text-white mb-2">{founder.name}</h3>
                    <p className="text-primary font-semibold text-base lg:text-lg">{founder.role}</p>
                  </div>
                  <div className="space-y-4">
                    <p className="text-gray-300 leading-relaxed text-sm lg:text-base">
                      {founder.education}
                    </p>
                    <p className="text-gray-300 leading-relaxed text-sm lg:text-base">
                      {founder.responsibility}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Products Section */}
        <section className="py-20 bg-black/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                💼 Featured Products & Projects
              </h2>
            </div>
            
            <div className="grid md:grid-cols-1 gap-8 max-w-4xl mx-auto">
              {products.map((product, index) => (
                <div key={index} className="bg-maitech-800/50 backdrop-blur rounded-xl p-8 border border-maitech-700/50">
                  <div className="flex flex-col md:flex-row md:items-center justify-between">
                    <div className="flex-1 mb-4 md:mb-0">
                      <h3 className="text-xl font-semibold text-white mb-2">
                        {product.title}
                        {product.description && (
                          <span className="text-gray-400"> {product.description}</span>
                        )}
                      </h3>
                    </div>
                    <div>
                      <a
                        href={product.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-primary hover:text-primary/80 transition-colors font-medium"
                      >
                        <Globe className="w-4 h-4 mr-2" />
                        {product.linkText}
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-20 bg-maitech-900/50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                📞 Contact Information
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-maitech-800/50 backdrop-blur rounded-xl p-8 border border-maitech-700/50">
                <h3 className="text-xl font-semibold text-white mb-6">Direct Contact</h3>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Mail className="w-5 h-5 text-primary mr-3" />
                    <a href="mailto:maitechnologies1@gmail.com" className="text-gray-300 hover:text-primary transition-colors">
                      maitechnologies1@gmail.com
                    </a>
                  </div>
                  <div className="flex items-center">
                    <Phone className="w-5 h-5 text-primary mr-3" />
                    <a href="tel:0777900721" className="text-gray-300 hover:text-primary transition-colors">
                      0777 900 721
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-maitech-800/50 backdrop-blur rounded-xl p-8 border border-maitech-700/50">
                <h3 className="text-xl font-semibold text-white mb-6">🌐 Social Media</h3>
                <div className="space-y-4">
                  {socialLinks.map((social, index) => (
                    <div key={index} className="flex items-center">
                      {social.icon ? (
                        <social.icon className="w-5 h-5 text-primary mr-3" />
                      ) : (
                        <div className="w-5 h-5 bg-primary rounded mr-3 flex items-center justify-center text-xs text-white font-bold">
                          {social.text?.[0]}
                        </div>
                      )}
                      <a 
                        href={social.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-gray-300 hover:text-primary transition-colors"
                      >
                        {social.name}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About; 